import React from 'react';
import { StyleSheet, View, Text, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import locationService from '../../services/locationService';

const MissionCard = ({ mission, onPress }) => {
  const navigation = useNavigation();
  const { user } = useSelector(state => state.auth);
  // Determinar el color según el estado de la misión
  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return '#f39c12'; // Amarillo
      case 'IN_PROGRESS':
        return '#3498db'; // Azul
      case 'COMPLETED':
        return '#2ecc71'; // Verde
      case 'CANCELLED':
        return '#e74c3c'; // Rojo
      default:
        return '#7f8c8d'; // Gris
    }
  };

  // Formatear la fecha
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Formatear el tiempo de vuelo estimado (de minutos a formato hh:mm)
  const formatFlightTime = (minutes) => {
    if (!minutes && minutes !== 0) return 'N/A';
    
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Abrir mapa con las coordenadas de la ubicación
  const openMap = async (locationId) => {
    try {
      
      // Intentar obtener detalles de la ubicación
      const details = await locationService.getLocationById(locationId);
      
      if (!details || (!details.geometry && !details.properties?.geometry)) {
        alert('No se pudieron obtener las coordenadas de la ubicación');
        return;
      }
      
      // Obtener las coordenadas
      let coordinates;
      if (details.geometry?.coordinates) {
        coordinates = details.geometry.coordinates[0];
      } else if (details.properties?.geometry?.coordinates) {
        coordinates = details.properties.geometry.coordinates[0];
      }
      
      if (!coordinates || !coordinates.length) {
        alert('No se encontraron coordenadas válidas');
        return;
      }
      
      // Calcular el centro
      let sumLat = 0;
      let sumLng = 0;
      coordinates.forEach(coord => {
        sumLng += coord[0];
        sumLat += coord[1];
      });
      const centerLat = sumLat / coordinates.length;
      const centerLng = sumLng / coordinates.length;
      
      // Crear URL y abrir
      const mapsUrl = `https://www.google.com/maps?q=${centerLat},${centerLng}`;
      console.log('URL del mapa:', mapsUrl);
      window.open(mapsUrl, '_blank');
    } catch (error) {
      alert('No se pudo abrir el mapa');
    }
  };

  return (
    <TouchableOpacity style={styles.card} onPress={onPress}>
      <View style={styles.header}>
        <Text style={styles.missionId}>Misión #{mission.sid || 'N/A'}</Text>
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(mission.status) }]}>
          <Text style={styles.statusText}>
            {mission.status === 'PENDING' ? 'Pendiente' :
             mission.status === 'IN_PROGRESS' ? 'En Progreso' :
             mission.status === 'COMPLETED' ? 'Completada' :
             mission.status === 'CANCELLED' ? 'Cancelada' : 'Desconocido'}
          </Text>
        </View>
      </View>
      
      <View style={styles.infoRow}>
        <View style={styles.iconWrapper}>
          <Ionicons name="business-outline" size={16} color="#7f8c8d" />
        </View>
        <Text style={styles.infoLabel}>Empresa:</Text>
        <Text style={styles.infoValue}>{mission.enterprise_name || 'No asignada'}</Text>
      </View>
      
      <View style={styles.infoRow}>
        <View style={styles.iconWrapper}>
          <Ionicons name="location-outline" size={16} color="#7f8c8d" />
        </View>
        <Text style={styles.infoLabel}>Ubicación:</Text>
        <View style={styles.locationContainer}>
          <Text style={styles.infoValue}>
            {mission.location_name || 
             (mission.location && typeof mission.location === 'object' ? 
               (mission.location.name || mission.location.properties?.name) : 
               (mission.location ? 'Ubicación ' + mission.location : 'No asignada'))}
          </Text>
          {mission.location && (
            <TouchableOpacity 
              style={styles.mapLink}
              onPress={(e) => {
                e.stopPropagation(); // Evitar que se propague al onPress del card
                openMap(mission.location);
              }}
            >
              <View style={styles.mapIconWrapper}>
                <Ionicons name="map-outline" size={14} color="#3498db" />
              </View>
              <Text style={styles.mapLinkText}>Ver en mapa</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
      
      <View style={styles.infoRow}>
        <View style={styles.iconWrapper}>
          <Ionicons name="airplane-outline" size={16} color="#7f8c8d" />
        </View>
        <Text style={styles.infoLabel}>Aeronave:</Text>
        <Text style={styles.infoValue}>{mission.aircraft_name || 'No asignada'}</Text>
      </View>
      
      <View style={styles.infoRow}>
        <View style={styles.iconWrapper}>
          <Ionicons name="flag-outline" size={16} color="#7f8c8d" />
        </View>
        <Text style={styles.infoLabel}>Objetivo:</Text>
        <Text style={[styles.infoValue, !mission.objectives_details || mission.objectives_details.length === 0 ? styles.criticalText : null]}>
          {mission.objectives_details && mission.objectives_details.length > 0 
            ? mission.objectives_details.map(obj => obj.name).join(', ')
            : 'Sin objetivo'
          }
        </Text>
      </View>
      
      <View style={styles.infoRow}>
        <View style={styles.iconWrapper}>
          <Ionicons name="person-outline" size={16} color="#7f8c8d" />
        </View>
        <Text style={styles.infoLabel}>Piloto:</Text>
        {mission.pilot_name && mission.pilot ? (
          <TouchableOpacity 
            onPress={() => {
              // Solo navegar si es usuario enterprise y misión completada
              if (user?.is_enterprise_user && mission.status === 'COMPLETED') {
                navigation.navigate('PilotProfile', {
                  pilotId: mission.pilot,
                  missionId: mission.id,
                  pilotName: mission.pilot_name
                });
              }
            }}
            disabled={!user?.is_enterprise_user || mission.status !== 'COMPLETED'}
          >
            <Text style={[
              styles.infoValue, 
              user?.is_enterprise_user && mission.status === 'COMPLETED' && styles.clickableText
            ]}>
              {mission.pilot_name}
            </Text>
          </TouchableOpacity>
        ) : (
          <Text style={styles.infoValue}>No asignado</Text>
        )}
      </View>
      
      <View style={styles.infoRow}>
        <View style={styles.iconWrapper}>
          <Ionicons name="time-outline" size={16} color="#7f8c8d" />
        </View>
        <Text style={styles.infoLabel}>Tiempo est.:</Text>
        <Text style={styles.infoValue}>{formatFlightTime(mission.estimated_flight_time)}</Text>
      </View>
      
      <View style={styles.footer}>
        <Text style={styles.date}>Creada: {formatDate(mission.created_at)}</Text>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  missionId: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  statusBadge: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  statusText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  iconWrapper: {
    marginRight: 6,
    width: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoLabel: {
    width: 80,
    fontSize: 14,
    color: '#7f8c8d',
  },
  infoValue: {
    flex: 1,
    fontSize: 14,
    color: '#2c3e50',
  },
  footer: {
    marginTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
    paddingTop: 10,
  },
  date: {
    fontSize: 12,
    color: '#7f8c8d',
    textAlign: 'right',
  },
  locationContainer: {
    flex: 1,
    flexDirection: 'column',
  },
  mapLink: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 2,
  },
  mapLinkText: {
    color: '#3498db',
    marginLeft: 4,
    fontSize: 12,
    textDecorationLine: 'underline',
  },
  mapIconWrapper: {
    width: 14,
    height: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  criticalText: {
    color: '#e74c3c',
    fontWeight: 'bold',
  },
});

export default MissionCard;